/**
 * AuthCallback — trades the one-time `session_id` for a real session.
 *
 * Google sends the user back to `{origin}/#session_id=...`. We hand that id
 * to our backend, which calls Emergent Auth server-side and sets the httpOnly
 * cookie. The id is single-use, hence the `useRef` guard: React StrictMode
 * runs effects twice in development and the second attempt would fail.
 */
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, AlertTriangle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const sessionId = new URLSearchParams(
      (location.hash || "").replace(/^#/, ""),
    ).get("session_id");

    if (!sessionId) {
      navigate("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        const { data } = await axios.post(
          `${API}/auth/session`,
          { session_id: sessionId },
          { withCredentials: true },
        );
        setUser(data?.user || null);
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/", { replace: true });
      } catch (err) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        // Sign-in is the one flow the owner cannot debug from the outside, so
        // the real cause goes on screen instead of a generic "riprova".
        console.error("[auth] session exchange failed", status, detail, err);
        if (status === 401) {
          setError(
            "Sessione Google non valida o scaduta. Riprova ad accedere.",
          );
        } else if (status === 503 || status === 504 || status === 502) {
          setError(
            `Il servizio di accesso non ha risposto (errore ${status}). ` +
            "Succede se il server si è appena riavviato: attendi qualche " +
            "secondo e riprova.",
          );
        } else if (!status) {
          setError(
            "Il server non è raggiungibile dal browser (nessuna risposta). " +
            "Controlla la connessione e riprova.",
          );
        } else {
          setError(
            `Accesso non riuscito — errore ${status}` +
            (detail ? ` · ${detail}` : ""),
          );
        }
      }
    })();
  }, [location.hash, navigate, setUser]);

  if (error) {
    return (
      <div
        data-testid="auth-callback-error"
        className="min-h-screen flex items-center justify-center bg-surface-subtle px-4"
      >
        <div className="max-w-md w-full bg-surface border border-line rounded-2xl p-6 space-y-4">
          <div className="flex items-center gap-2 text-warning">
            <AlertTriangle className="w-5 h-5" />
            <span className="text-base font-medium">Accesso non completato</span>
          </div>
          <p className="text-sm text-content-secondary">{error}</p>
          <button
            data-testid="auth-callback-retry"
            onClick={() => navigate("/login", { replace: true })}
            className="w-full py-2.5 rounded-lg bg-brand hover:bg-brand-hover text-content-on-brand text-sm font-medium transition-colors duration-base ease-system"
          >
            Torna all'accesso
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="auth-callback"
      className="min-h-screen flex items-center justify-center bg-surface-subtle"
    >
      <div className="flex items-center gap-3 text-content-secondary text-sm">
        <Loader2 className="w-5 h-5 animate-spin text-brand-accent" />
        Completamento accesso…
      </div>
    </div>
  );
}

export default AuthCallback;
